import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const BASE_URL = (import.meta as any).env?.VITE_API_URL ?? "";

interface TransferMeta {
  id: string;
  description: string;
  status: "ready" | "pending";
  expiresAt: string;
  fileName: string | null;
}

export default function DownloadPage() {
  const { token } = useParams<{ token: string }>();

  const [meta, setMeta] = useState<TransferMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!token) return;

    fetch(`${BASE_URL}/public/download/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          let msg = res.statusText;
          try {
            const body = await res.json();
            msg = (body as { error: string }).error ?? msg;
          } catch {}
          setError(msg);
          return;
        }
        const data = await res.json();
        setMeta(data as TransferMeta);
      })
      .catch(() => setError("Network error. Please try again."));
  }, [token]);

  async function handleDownload() {
    if (!token) return;
    setDownloading(true);
    try {
      const res = await fetch(`${BASE_URL}/public/download/${token}/file`);
      if (!res.ok) {
        let msg = res.statusText;
        try {
          const body = await res.json();
          msg = (body as { error: string }).error ?? msg;
        } catch {}
        setError(msg);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = meta?.fileName ?? "download";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Download failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy-950 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        <h1 className="text-2xl font-bold text-navy-950 mb-1">SeaVault</h1>
        <p className="text-sm text-gray-500 mb-8">Secure Document Transfer</p>

        {error ? (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : meta ? (
          <>
            <p className="text-gray-700 mb-2 font-medium">{meta.description}</p>
            {meta.fileName && (
              <p className="text-xs text-gray-500 mb-2">{meta.fileName}</p>
            )}
            {meta.expiresAt && (
              <p className="text-xs text-gray-400 mb-6">
                Expires: {new Date(meta.expiresAt).toUTCString()}
              </p>
            )}
            {meta.status === "pending" ? (
              <p className="text-sm text-yellow-600">
                Files are not yet available. Please check back later.
              </p>
            ) : (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="w-full rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
              >
                {downloading ? "Downloading…" : "Download files"}
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-400">Loading…</p>
        )}
      </div>
    </div>
  );
}
